import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";
import { relatedByCluster } from "@/lib/clusters";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Stockholm?", a: "Ansökan görs till Socialförvaltningen, tillståndsenheten, i Stockholms stad. Verksamhet i annan kommun (t.ex. Solna, Nacka, Sundbyberg) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Stockholm?", a: "Stockholms stad tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Stockholm?", a: "Stockholms stad anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
  { q: "Vilka dokument behöver jag bifoga?", a: "Bolagsuppgifter, finansieringsplan, ritning över serveringsytan, hyresavtal, meny (om relevant) samt uppgifter om PBI-kretsen och deras lämplighetsunderlag." },
];

const URL = "https://alkoholprovet.se/serveringstillstand-stockholm";

export const Route = createFileRoute("/serveringstillstand-stockholm")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Stockholm" },
      ]}
      title="Serveringstillstånd Stockholm – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Stockholm: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.stockholm, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Stockholms stad – Socialförvaltningens tillståndsenhet",
        address: "Swedenborgsgatan 20, 118 27 Stockholm",
        phone: "08-508 25 000",
        waitTime: "8–12 veckor från komplett ansökan (stadigvarande); tillfälligt tillstånd snabbare",
        feeExamples: [
          "Stadigvarande tillstånd allmänheten: ansökningsavgift ca 14 000 kr",
          "Tillfälligt tillstånd allmänheten: ca 7 000–10 000 kr per tillfälle",
          "Årlig tillsynsavgift: fast del + rörlig baserat på alkoholomsättning",
        ],
        checklist: [
          "Registreringsbevis och bolagsordning",
          "Finansieringsplan med ursprung för insatt kapital",
          "Hyresavtal eller intyg om nyttjanderätt",
          "Ritning över serveringsyta och uteservering",
          "Uppgifter om PBI-kretsen + godkänt kunskapsprov",
          "Brandskyddsdokumentation",
        ],
        officialUrl: KOMMUN_LINKS.stockholm,
      }}
      sections={[
        { h2: "Steg-för-steg: ansökan i Stockholm", body: "1. Förbered dokumenten i checklistan ovan.\n2. Skapa konto i Stockholms stads e-tjänst för serveringstillstånd.\n3. Fyll i ansökan – välj typ (stadigvarande till allmänheten, tillfälligt, slutet sällskap, catering etc.).\n4. Betala ansökningsavgiften – handläggning startar först då.\n5. Boka och genomför kunskapsprovet för minst en PBI.\n6. Avvakta remissvar från polis, miljöförvaltning och skatteverket.\n7. Beslut meddelas skriftligt; godkänt tillstånd publiceras i kommunens register." },
        { h2: "Krav från Stockholms stad", body: "Personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Stockholms innerstad har särskilda riktlinjer för uteserveringar och serveringstider, särskilt på Södermalm och i City." },
        { h2: "Kunskapsprovet i Stockholm", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun (FoHMFS 2014:7). Med Alkoholprovet kan du träna i samma format hemifrån." },
        { h2: "Tillsyn och uppföljning", body: "Stockholms stad bedriver aktiv tillsyn på serveringsställen. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag." },
      ]}
      faqs={faqs}
      related={relatedByCluster("/serveringstillstand-stockholm")}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Stockholm 2026 – så ansöker du" },
      { name: "description", content: "Steg-för-steg-guide till serveringstillstånd i Stockholm: avgifter, handläggningstid och tips inför kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd i Stockholm 2026" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Stockholm." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Stockholm", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Serveringstillstånd Stockholm – ansökan och krav",
        description: "Komplett guide till serveringstillstånd i Stockholm: var du ansöker, krav, avgifter, handläggningstid och kunskapsprov.",
        datePublished: "2026-01-15",
        dateModified: LAST_REVIEWED,
      }),
    ],
  }),
});
